import { useMemo, useState } from 'react';
import { EMAIL_RE, userLabel } from './agentManagerUtils';

const initialAgents = (initial, agentOptions) =>
  initial
    ? [agentOptions.find((a) => a.agentCode === initial.agentCode) || { agentCode: initial.agentCode, agentName: initial.agentName }]
    : [];

const initialManager = (initial, users) =>
  initial
    ? users.find((u) => (u.Email || '').toLowerCase() === initial.managerEmail) || {
        Name: initial.managerName,
        Email: initial.managerEmail,
      }
    : null;

// State + validation of the add/edit agent→manager dialog.
// `initial` = an existing row when editing, null when adding.
const useAgentManagerForm = ({ initial, rows, users, agentOptions }) => {
  const [selectedAgents, setSelectedAgents] = useState(() => initialAgents(initial, agentOptions));
  const [manager, setManager] = useState(() => initialManager(initial, users));
  const [branches, setBranches] = useState(initial?.branches || []);
  const [allBranchesSelected, setAllBranchesSelected] = useState(!initial?.branches?.length);
  const managerEmail = (manager?.Email || '').toLowerCase();

  // Agents already under the chosen manager aren't offered again when adding.
  const availableAgents = useMemo(() => {
    if (initial) return agentOptions;
    const assigned = new Set(rows.filter((r) => r.managerEmail === managerEmail).map((r) => r.agentCode));
    return agentOptions.filter((a) => !assigned.has(a.agentCode));
  }, [initial, agentOptions, rows, managerEmail]);

  // Other managers the selected agents already have (allowed — e.g. a
  // different manager per branch — but worth showing).
  const otherManagers = useMemo(
    () =>
      selectedAgents.flatMap((a) =>
        rows
          .filter((r) => r.agentCode === a.agentCode && r._id !== initial?._id && r.managerEmail !== managerEmail)
          .map((r) => `${a.agentName || a.agentCode} ← ${r.managerName}`),
      ),
    [selectedAgents, rows, initial, managerEmail],
  );

  const isValid =
    selectedAgents.length > 0 && !!manager && EMAIL_RE.test(managerEmail) && (allBranchesSelected || branches.length > 0);

  const toPayload = () => ({
    agents: selectedAgents.map((a) => ({ agentCode: a.agentCode, agentName: a.agentName })),
    branches: allBranchesSelected ? [] : branches,
    managerName: userLabel(manager),
    managerEmail,
  });

  return {
    fields: { selectedAgents, manager, branches, allBranchesSelected },
    setters: { setSelectedAgents, setManager, setBranches, setAllBranchesSelected },
    availableAgents,
    otherManagers,
    isValid,
    toPayload,
  };
};

export default useAgentManagerForm;
